import { useContext } from 'react'
import { useParams } from 'react-router-dom'
import { ModelPromiseClient } from '@trustero/trustero-api-web/lib/model/model_grpc_web_pb'
import { PolicyPromiseClient } from '@trustero/trustero-api-web/lib/model/policy_grpc_web_pb'
import { ListPoliciesRequest } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useVendors } from 'src/pages/Vendors/vendors.hooks'
import { useRisks } from 'src/pages/Risks/risks.hooks'
import { useControlStatuses } from 'src/pages/AccountSettings/AccountSettings.hooks'
import { useAuthorizedGrpcClient } from '../../../../adapter'
import { useDepartments } from '../../Department/useDepartments'
import {
  DownloadControlsReport,
  ReportExportFormat,
} from '../../../../Utils/reports/ControlsReport'
import { AuditContext } from '../../../../context/AuditContext'
import { useAudit } from '../audit'
import { boolValue, stringValue } from '../../../../Utils/primitives'
import { useComplianceFrameworks } from '../../complianceframework'
import { useComplianceFrameworkObjectives } from '../../Framework/useComplianceFrameworkObjectives'

// Controls and policies are fetched when the download is confirmed rather than on mount.
// The confirmation modal awaits this callback and disables its button while
// it runs, so the user waits on a spinner instead of receiving a half-empty csv.
export const useDownloadControlsReport = (
  auditId?: string,
  format: ReportExportFormat = 'csv',
): (() => Promise<void>) => {
  const departments = useDepartments()
  const { pageContext } = useParams()
  const { auditId: currentAuditId } = useContext(AuditContext)
  const { data: audit } = useAudit(auditId ?? '', !!auditId)
  const { data: complianceFrameworks } = useComplianceFrameworks(true)
  const { data: cfObjectives } = useComplianceFrameworkObjectives(
    complianceFrameworks?.getItemsList()?.map((cf) => cf.getId()) ?? [],
  )
  const { data: vendors } = useVendors()
  const { data: risksData } = useRisks()
  const { data: controlStatuses } = useControlStatuses()
  const modelClient = useAuthorizedGrpcClient(ModelPromiseClient)
  const policyClient = useAuthorizedGrpcClient(PolicyPromiseClient)

  const invalidAuditId = auditId && !audit
  if (invalidAuditId) {
    return () => Promise.resolve()
  }

  return async () => {
    const [controls, policies] = await Promise.all([
      modelClient.listControls(
        new ListControlsRequest()
          .setIsDismissed(boolValue(false))
          .setAuditId(stringValue(auditId ?? currentAuditId))
          .setUseNewModel(true)
          // the csv is the only reader of procedure/notes/suggestions
          .setIncludeDetails(true),
      ),
      policyClient.list(new ListPoliciesRequest().setUseNewModel(true)),
    ])

    const controlPolicyNamesByControlId = controls
      .getItemsList()
      .reduce((acc: Record<string, string[]>, control) => {
        acc[control.getId()] = [
          ...new Set(
            control
              .getLinkedPoliciesList()
              .map((policy) => policy.getPolicyName())
              .filter(Boolean),
          ),
        ]
        return acc
      }, {})

    // One department entry per linked policy means a control with several policies
    // in the same department would repeat it; dedupe so the export lists it once.
    const controlDepartmentNamesByControlId = controls
      .getItemsList()
      .reduce((acc: Record<string, string[]>, control) => {
        acc[control.getId()] = [
          ...new Set(
            control
              .getLinkedPoliciesList()
              .map((policy) => policy.getDepartment())
              .filter(Boolean),
          ),
        ]
        return acc
      }, {})

    return DownloadControlsReport(
      controls,
      policies,
      controlPolicyNamesByControlId,
      controlDepartmentNamesByControlId,
      departments,
      vendors?.getVendorsList(),
      complianceFrameworks,
      cfObjectives,
      risksData?.getRisksList(),
      controlStatuses?.getItemsList(),
      undefined,
      pageContext ?? '',
      undefined,
      format,
    )
  }
}
